import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { useState, useMemo } from 'react';

// Material UI
import { Grid, Box, Typography, IconButton, Icon, Stack, Divider, Tooltip, Link } from '@mui/material';

// Componentes
import Download from '@components/Downloads/Download';

// Middleware
import middleware from '@middlewares/middleware';

// Services
import EventsServices from '@services/EventsServices';

// Utils
import { Swal, SwalQR } from '@utils/alerts';
import { URL_PUBLICA_FRONT } from '@utils/global';

const CardEvent = (props) => {
  const {
    post,
    setType = null,
    handleDelete = null,
    canModify = false,
    URL = '',
    handleReconocimientos = () => {},
  } = props;

  const navigate = useNavigate();

  const preregistroURL = URL_PUBLICA_FRONT + post.URL;

  const handleShare = () => window.open(preregistroURL, '_blank', 'noopener,noreferrer');

  const [isDownload, setIsDownload] = useState(false);

  const handleExport = async () => {
    try {
      setIsDownload(true);
      const result = await EventsServices.exportEventosDetails({
        idEvento: post.id,
        Actividad: post.Actividad,
        FechaInicio: post.FechaInicio,
      });
      const { results, message } = result;

      if (results) Swal.fire({ title: message, icon: 'success' });
      else throw new Error(message);
    } catch (e) {
      Swal.fire({ title: e.message, icon: 'warning' });
    } finally {
      setIsDownload(false);
    }
  };

  const sesiones = useMemo(() => post.sesiones?.split(',') || [post.FechaFin], [post]);

  const hoy = new Date();
  const fechaHoy = new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate());

  const fechaFin = new Date(sesiones[sesiones.length - 1]);
  const fechaPost = new Date(fechaFin.getFullYear(), fechaFin.getMonth(), fechaFin.getDate());

  const YaPasoDia = fechaPost < fechaHoy;
  const YaPasoHora = fechaFin < hoy;

  return (
    <Grid container spacing={2} textAlign={'center'}>
      <Grid item xs={12}>
        <Typography gutterBottom variant="h5" component="div">
          {post.Actividad}
        </Typography>
      </Grid>

      {post.Descripcion && (
        <Grid item xs={12}>
          <Stack direction="row" divider={<Divider orientation="vertical" flexItem />} spacing={2}>
            <Icon>notes</Icon>
            <Typography variant="body1" align="left">
              {post.Descripcion}
            </Typography>
          </Stack>
        </Grid>
      )}

      {/* {post.FechaInicio && (
        <Grid item xs={12}>
          <Stack direction="row" divider={<Divider orientation="vertical" flexItem />} spacing={2}>
            <Icon>today</Icon>
            <Typography variant="h6">{post.FechaInicio}</Typography>
          </Stack>
        </Grid>
      )} */}

      {post.NombreLugar && (
        <Grid item xs={12}>
          <Stack direction="row" divider={<Divider orientation="vertical" flexItem />} spacing={2}>
            <Icon>location_on</Icon>
            <Typography variant="h6" align="left">
              {[
                post.NombreLugar?.trim(),
                post.Calle?.trim(),
                post.NumExt ? `#${String(post.NumExt).trim()}` : null,
                post.Colonia?.trim(),
              ]
                .filter(Boolean) // elimina null, undefined y strings vacíos
                .join(', ')}
            </Typography>
          </Stack>
        </Grid>
      )}

      {post.LigaVirtual && (
        <Grid item xs={12}>
          <Stack direction="row" divider={<Divider orientation="vertical" flexItem />} spacing={2}>
            <Icon>link</Icon>
            <Link href={post.LigaVirtual} target="_blank" rel="noopener noreferrer" underline="none">
              <Typography variant="h6" align="left">
                {post.LigaVirtual}
              </Typography>
            </Link>
          </Stack>
        </Grid>
      )}

      <Grid
        item
        xs={12}
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 2,
        }}
      >
        <Box>
          {sesiones.length === 1 ? (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                <strong>Fecha del evento:</strong> {sesiones[0]}
              </Typography>
            </Box>
          ) : (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Inicio del evento:</strong> {sesiones[0]}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Fin del evento:</strong> {sesiones[sesiones.length - 1]}
                </Typography>
              </Box>
            </>
          )}
        </Box>

        <Box
          sx={{
            flexGrow: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'end',
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          {canModify && (
            <>
              <Tooltip title="Generar Reconocimientos" disableInteractive arrow>
                <IconButton
                  size="small"
                  aria-label="edit"
                  color="secondary"
                  onClick={() => {
                    handleReconocimientos({}, 1);
                  }}
                >
                  <Icon>description</Icon>
                </IconButton>
              </Tooltip>
              {
                <Tooltip title="Exportar datos" disableInteractive arrow>
                  <IconButton size="small" aria-label="edit" color="primary" onClick={handleExport}>
                    <Icon>download</Icon>
                  </IconButton>
                </Tooltip>
              }

              <Tooltip title="Ver Dashboard" disableInteractive arrow>
                <IconButton
                  size="small"
                  aria-label="edit"
                  color="secondary"
                  onClick={() => navigate(`${URL}dashboard/${post.id}`)}
                >
                  <Icon>bar_chart</Icon>
                </IconButton>
              </Tooltip>

              {!YaPasoDia && (
                <>
                  <Tooltip title="Ver QR de asistencia general" disableInteractive arrow>
                    <IconButton
                      size="small"
                      aria-label="qr-asistencia"
                      color="primary"
                      onClick={() => SwalQR({ title: `QR asistencia de ${post.Actividad}`, QR: post.UUID })}
                    >
                      <Icon>qr_code_scanner</Icon>
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Asistencia Manual" disableInteractive arrow>
                    <IconButton
                      size="small"
                      aria-label="edit"
                      color="info"
                      onClick={() => navigate(`${URL}pase-lista/${post.id}`)}
                    >
                      <Icon>front_hand</Icon>
                    </IconButton>
                  </Tooltip>
                </>
              )}

              {middleware.checkMenuAction('Agregar') && !YaPasoDia && (
                <Tooltip title="Agregar Staff" disableInteractive arrow>
                  <IconButton
                    size="small"
                    aria-label="edit"
                    color="secondary"
                    onClick={() => navigate(`${URL}staff/${post.id}`)}
                  >
                    <Icon>groups</Icon>
                  </IconButton>
                </Tooltip>
              )}

              {middleware.checkMenuAction('Eliminar') && handleDelete && !YaPasoHora && (
                <Tooltip title="Eliminar evento" disableInteractive arrow>
                  <IconButton size="small" aria-label="edit" color="error" onClick={() => handleDelete(post)}>
                    <Icon>delete</Icon>
                  </IconButton>
                </Tooltip>
              )}

              {middleware.checkMenuAction('Editar') && setType && !YaPasoHora && (
                <Tooltip title="Editar evento" disableInteractive arrow>
                  <IconButton size="small" aria-label="edit" color="success" onClick={() => setType(3)}>
                    <Icon>edit</Icon>
                  </IconButton>
                </Tooltip>
              )}

              {middleware.checkMenuAction('Agregar') && !YaPasoDia && !post.TienePreregistro && (
                <Tooltip title="Agregar invitados" disableInteractive arrow>
                  <IconButton
                    size="small"
                    aria-label="edit"
                    color="secondary"
                    onClick={() => navigate(`${URL}invitados/${post.id}`)}
                  >
                    <Icon>add</Icon>
                  </IconButton>
                </Tooltip>
              )}

              {post.TienePreregistro === 1 && !YaPasoDia && (
                <>
                  <Tooltip title="Ver QR del registro" disableInteractive arrow>
                    <IconButton
                      size="small"
                      aria-label="qr-registro"
                      color="primaryDark"
                      onClick={() =>
                        SwalQR({ title: `QR registro de ${post.Actividad}`, QR: preregistroURL })
                      }
                    >
                      <Icon>qr_code_24</Icon>
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Compartir Preregistro" disableInteractive arrow>
                    <IconButton
                      size="small"
                      aria-label="shares"
                      onClick={handleShare}
                      disabled={!post.TienePreregistro}
                    >
                      <Icon>share</Icon>
                    </IconButton>
                  </Tooltip>
                </>
              )}
            </>
          )}
        </Box>
      </Grid>

      {isDownload && (
        <Grid item xs={12}>
          <Box marginBottom={2}>
            <Download format="xlsx" isDownload={isDownload} />
          </Box>
        </Grid>
      )}
    </Grid>
  );
};

CardEvent.propTypes = {
  post: PropTypes.object.isRequired,
  index: PropTypes.number,
};

export default CardEvent;
